import { useState } from 'react';

export type OneTimeOrderShippingOption = 'standard' | 'free' | 'custom';

export interface PricedLine {
  id: string;
  name: string;
  price: number; // base price (per bundle for bundles)
  quantity: number;
}

export interface PricedLineResult {
  basePrice: number;
  unitPrice: number;
  lineTotal: number;
  isCustom: boolean;
  isMemberPriced: boolean;
}

export const MEMBER_DISCOUNT_RATE = 0.1;
// Mock rate for the subscription's own delivery method
export const STANDARD_SHIPPING_AMOUNT = 15;

const parseAmount = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export function useOneTimeOrderPricing(lines: PricedLine[]) {
  const [customPrices, setCustomPrices] = useState<Record<string, number>>({});
  const [memberDiscountOn, setMemberDiscountOn] = useState(true);
  const [shippingOption, setShippingOption] = useState<OneTimeOrderShippingOption>('standard');
  const [customShipping, setCustomShipping] = useState('');
  const [isOrderTotalOpen, setIsOrderTotalOpen] = useState(false);
  const [orderTotal, setOrderTotal] = useState('');
  const [reason, setReason] = useState('');

  const reset = () => {
    setCustomPrices({});
    setMemberDiscountOn(true);
    setShippingOption('standard');
    setCustomShipping('');
    setIsOrderTotalOpen(false);
    setOrderTotal('');
    setReason('');
  };

  const setCustomPrice = (id: string, price: number) => {
    setCustomPrices(prev => ({
      ...prev,
      [id]: price
    }));
  };
  const resetCustomPrice = (id: string) => {
    setCustomPrices(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
  };

  const openOrderTotal = () => setIsOrderTotalOpen(true);
  const removeOrderTotal = () => {
    setIsOrderTotalOpen(false);
    setOrderTotal('');
  };

  const getLine = (line: PricedLine): PricedLineResult => {
    const custom = customPrices[line.id];
    const isCustom = custom !== undefined;
    const isMemberPriced = memberDiscountOn && !isCustom;
    const unitPrice = isCustom ? custom : isMemberPriced ? line.price * (1 - MEMBER_DISCOUNT_RATE) : line.price;
    return {
      basePrice: line.price,
      unitPrice,
      lineTotal: unitPrice * line.quantity,
      isCustom,
      isMemberPriced
    };
  };

  // Item total is at full (or custom) prices; the member discount is shown separately
  const itemTotal = lines.reduce((sum, line) => sum + (customPrices[line.id] ?? line.price) * line.quantity, 0);
  const memberDiscount = memberDiscountOn ? lines.reduce((sum, line) => customPrices[line.id] === undefined ? sum + line.price * line.quantity * MEMBER_DISCOUNT_RATE : sum, 0) : 0;
  // Only a real typed value overrides the totals, never a placeholder
  const orderTotalValue = isOrderTotalOpen ? parseAmount(orderTotal) : null;
  const hasOrderTotal = orderTotalValue !== null;
  const orderAdjustment = hasOrderTotal ? orderTotalValue - itemTotal : 0;
  const goodsTotal = hasOrderTotal ? orderTotalValue : itemTotal - memberDiscount;
  const shipping = shippingOption === 'standard' ? STANDARD_SHIPPING_AMOUNT : shippingOption === 'free' ? 0 : parseAmount(customShipping) ?? 0;
  const total = goodsTotal + shipping;

  const hasCustomPrices = lines.some(line => customPrices[line.id] !== undefined);
  const hasChanges = hasCustomPrices || hasOrderTotal || !memberDiscountOn || shippingOption !== 'standard';

  return {
    memberDiscountOn,
    setMemberDiscountOn,
    shippingOption,
    setShippingOption,
    customShipping,
    setCustomShipping,
    isOrderTotalOpen,
    openOrderTotal,
    removeOrderTotal,
    orderTotal,
    setOrderTotal,
    reason,
    setReason,
    setCustomPrice,
    resetCustomPrice,
    getLine,
    hasOrderTotal,
    itemTotal,
    memberDiscount,
    orderAdjustment,
    shipping,
    total,
    hasChanges,
    reset
  };
}

export type OneTimeOrderPricing = ReturnType<typeof useOneTimeOrderPricing>;
